import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { HUB, CATEGORIES, PARTNER_FAQ, FLEET_SCALE } from "../lib/partnerContent";
import { useSeo, breadcrumb, faqSchema } from "../lib/seo";
import { Building2, Plane, Globe2, Briefcase, ShieldCheck, Gift, Megaphone, MessageSquare, ArrowRight } from "lucide-react";

const ICONS = { "hotels-hospitality": Building2, "travel-tourism": Plane, "digital-aggregators": Globe2, "corporate-mobility": Briefcase, "insurance-roadside": ShieldCheck, "loyalty-programs": Gift, "affiliate-sales": Megaphone, connect: MessageSquare };

export default function PartnerWithUs() {
  const { t } = useTranslation();
  useSeo({
    title: "Partner With Europcar | B2B Car Rental Partnerships UAE",
    description: `Hotels, travel trade, aggregators, corporates, insurers, loyalty and affiliates — build a rental programme with Europcar UAE. ${FLEET_SCALE}.`,
    jsonLd: [breadcrumb([["Home", "/"], ["Partner With Us", "/partner-with-us"]]), faqSchema(PARTNER_FAQ)],
  });

  return (
    <div className="min-h-screen flex flex-col bg-[#fafcfa]">
      <Header />
      <div className="relative bg-[#0F1E17]">
        <img src={HUB.image} alt="Europcar vehicle waiting at a Dubai hotel entrance" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <div className="promo-stripe max-w-2xl">
            <h1 className="heading-upper text-3xl sm:text-4xl text-white" data-testid="partner-hub-heading">{t(HUB.headline)}</h1>
            <p className="text-white/85 text-sm mt-3 leading-relaxed">{t(HUB.sub)}</p>
          </div>
          <a href="#categories" className="btn-cta rounded px-6 py-3 text-xs inline-flex items-center gap-2 mt-6" data-testid="partner-hub-cta">{t("Choose your category")} <ArrowRight className="w-4 h-4" /></a>
        </div>
      </div>

      <div className="bg-[#144733]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 grid sm:grid-cols-3 gap-3 text-center" data-testid="partner-value-strip">
          {HUB.valueStrip.map((v) => <div key={v} className="text-xs text-white/90 font-medium">{t(v)}</div>)}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12 w-full flex-1 space-y-14">
        <section id="categories" data-testid="partner-categories-grid">
          <div className="promo-stripe mb-6">
            <h2 className="heading-upper text-xl text-[#144733]">{t("Find your partnership")}</h2>
            <p className="text-sm text-[#57685e] mt-1">{t("Eight proven programme types — pick the one closest to your business.")}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CATEGORIES.map((c) => {
              const Icon = ICONS[c.slug];
              return (
                <Link key={c.slug} to={`/partner-with-us/${c.slug}`} className="bg-white border border-[#E2E8F0] rounded-2xl p-5 card-lift group" data-testid={`partner-category-${c.slug}`}>
                  <Icon className="w-6 h-6 text-[#009900] mb-3" />
                  <h3 className="font-bold text-sm text-[#0F1E17]">{t(c.label)}</h3>
                  <p className="text-xs text-[#57685e] mt-1 line-clamp-2">{t(c.sub)}</p>
                  <span className="text-[11px] font-semibold text-[#009900] mt-3 inline-flex items-center gap-1 group-hover:gap-2 transition-all">{t("Explore")} <ArrowRight className="w-3 h-3" /></span>
                </Link>
              );
            })}
          </div>
        </section>

        <section data-testid="partner-why">
          <div className="promo-stripe mb-6"><h2 className="heading-upper text-xl text-[#144733]">{t("Why partner with Europcar")}</h2></div>
          <div className="grid sm:grid-cols-3 gap-4">
            {HUB.why.map((w) => (
              <div key={w.title} className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
                <h3 className="font-bold text-sm text-[#0F1E17]">{t(w.title)}</h3>
                <p className="text-xs text-[#57685e] mt-2 leading-relaxed">{t(w.text)}</p>
              </div>
            ))}
          </div>
        </section>

        <section data-testid="partner-how">
          <div className="promo-stripe mb-6"><h2 className="heading-upper text-xl text-[#144733]">{t("How it works")}</h2></div>
          <div className="grid sm:grid-cols-3 gap-4">
            {HUB.how.map((h, i) => (
              <div key={h.step} className="bg-[#f0f9f0] border border-[#009900]/15 rounded-2xl p-5 relative">
                <span className="absolute top-4 right-4 text-3xl font-bold text-[#009900]/15">{i + 1}</span>
                <h3 className="font-bold text-sm text-[#144733]">{t(h.step)}</h3>
                <p className="text-xs text-[#41504a] mt-2 leading-relaxed">{t(h.text)}</p>
              </div>
            ))}
          </div>
        </section>

        <section data-testid="partner-faq">
          <div className="promo-stripe mb-6"><h2 className="heading-upper text-xl text-[#144733]">{t("Partner FAQ")}</h2></div>
          <div className="bg-white border border-[#e2ebe4] rounded-2xl divide-y divide-[#f1f3f1]">
            {PARTNER_FAQ.map((f) => (
              <details key={f.q} className="p-4 group">
                <summary className="text-sm font-semibold text-[#0F1E17] cursor-pointer list-none flex justify-between items-center">{t(f.q)}<span className="text-[#009900] group-open:rotate-45 transition-transform text-lg leading-none">+</span></summary>
                <p className="text-xs text-[#57685e] mt-2 leading-relaxed">{t(f.a)}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="bg-[#144733] rounded-2xl p-8 sm:p-10 text-center" data-testid="partner-closing-cta">
          <h2 className="heading-upper text-2xl text-white">{t(HUB.closing.headline)}</h2>
          <p className="text-white/80 text-sm mt-2">{t(HUB.closing.text)}</p>
          <a href="#categories" className="btn-cta rounded px-8 py-3 text-xs inline-flex mt-5">{t(HUB.closing.cta)}</a>
        </section>
      </main>
      <Footer />
    </div>
  );
}
